import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../api';

export default function NewTask() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [descriptionSuggestions, setDescriptionSuggestions] = useState([]);

  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState([]);
  const [startAt, setStartAt] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [repeatInterval, setRepeatInterval] = useState('');
  const [repeatCount, setRepeatCount] = useState(2);
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.listAssignableUsers().then(setUsers).catch(() => toast.error("Couldn't load the user list."));
    api.listTemplates().then(setTemplates).catch(() => toast.error("Couldn't load templates."));
    api.listSuggestions('title').then(setTitleSuggestions).catch(() => {});
    api.listSuggestions('description').then(setDescriptionSuggestions).catch(() => {});
  }, []);

  function handleTemplateChange(id) {
    setTemplateId(id);
    const template = templates.find((t) => String(t.id) === id);
    if (!template) return;
    setTitle(template.name);
    setDescription(template.description || '');
    setRepeatInterval(template.repeat_interval || '');
    if (template.repeat_interval) setRepeatCount(template.repeat_count || 2);
  }

  function toggleAssignee(id) {
    setAssignedTo((current) =>
      current.includes(id) ? current.filter((existing) => existing !== id) : [...current, id]
    );
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim() || assignedTo.length === 0) {
      toast.error('Title and at least one assignee are required.');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      if (description) formData.append('description', description);
      formData.append('assigned_to', JSON.stringify(assignedTo));
      // <input type="datetime-local"> has no timezone — treated as local time
      // by `new Date()`, then serialized to a proper ISO string with offset.
      if (startAt) formData.append('start_at', new Date(startAt).toISOString());
      if (dueAt) formData.append('due_at', new Date(dueAt).toISOString());
      if (repeatInterval) {
        formData.append('repeat_interval', repeatInterval);
        formData.append('repeat_count', repeatCount);
      }
      if (photo) formData.append('photo', photo);

      const { tasks } = await api.createTask(formData);
      toast.success(tasks.length === 1 ? 'Task created' : `${tasks.length} tasks created`);
      navigate('/');
    } catch {
      toast.error("Couldn't create the task.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <h1>New task</h1>
      <form className="card" onSubmit={handleSubmit}>
        {templates.length > 0 && (
          <label>
            Template
            <select value={templateId} onChange={(e) => handleTemplateChange(e.target.value)}>
              <option value="">None</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
        )}
        <label>
          Title
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            list="title-suggestions"
            required
          />
          <datalist id="title-suggestions">
            {titleSuggestions.map((s) => <option key={s} value={s} />)}
          </datalist>
        </label>
        <label>
          Description
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            list="description-suggestions"
          />
          <datalist id="description-suggestions">
            {descriptionSuggestions.map((s) => <option key={s} value={s} />)}
          </datalist>
        </label>
        <fieldset className="checkbox-group">
          <legend>Assign to</legend>
          {users.map((u) => (
            <label key={u.id} className="checkbox-label">
              <input
                type="checkbox"
                checked={assignedTo.includes(u.id)}
                onChange={() => toggleAssignee(u.id)}
              />
              {u.name}
            </label>
          ))}
        </fieldset>
        <label>
          Start date/time (optional)
          <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} />
        </label>
        <label>
          Due date/time (optional)
          <input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
        </label>
        <label>
          Repeat
          <select value={repeatInterval} onChange={(e) => setRepeatInterval(e.target.value)}>
            <option value="">Doesn't repeat</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </label>
        {repeatInterval && (
          <label>
            Number of times
            <input
              type="number"
              min={2}
              value={repeatCount}
              onChange={(e) => setRepeatCount(e.target.value)}
            />
          </label>
        )}
        <label>
          Photo (optional)
          <input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files[0] || null)} />
        </label>
        <button type="submit" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create task'}
        </button>
      </form>
    </div>
  );
}
